import React, { useCallback, useEffect, useState } from 'react';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import clinicService from '../../../services/clinic.service';
import { useNavigate, Link } from 'react-router-dom';
import UserReminder from '../../pages/settings/users/UserReminder';
import EcxUserProfileModal from './EcxUserProfileModal';
import { countNewInvoices, INVOICE_QUERY } from '../../pages/invoices/invoiceUtils';
import { getNavIconClass } from '../../../navigation/clinicNavConfig';
import './EcxOffcanvas.css';

const PanelActionIcon = ({ icon, themeKey }) => (
    <span className={`ecx-nav-icon ${getNavIconClass(themeKey)} ecx-user-panel__action-icon`}>
        <i className={`bi ${icon}`} />
    </span>
);


const getInitials = (userInfo) => {
    const first = userInfo?.firstName?.[0] || '';
    const last = userInfo?.lastName?.[0] || '';
    const initials = `${first}${last}`.trim().toUpperCase();

    return initials || null;
};

const formatRoleLabel = (role) => {
    if (!role) {
        return null;
    }

    return role
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const UserStatusPills = ({ user }) => {
    if (!user) {
        return null;
    }

    return (
        <div className="ecx-user-panel__status-group">
            {user.isBlocked && (
                <span className="ecx-user-panel__status-pill is-closed">
                    <i className="bi bi-slash-circle" />
                    Blocked
                </span>
            )}
            <span className={`ecx-user-panel__status-pill ${user.isActive ? 'is-open' : 'is-closed'}`}>
                <i className={`bi ${user.isActive ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`} />
                {user.isActive ? 'Active' : 'Inactive'}
            </span>
            <span className={`ecx-user-panel__status-pill ${user.isAuthenticated ? 'is-active' : 'is-closed'}`}>
                <i className={`bi ${user.isAuthenticated ? 'bi-shield-check' : 'bi-clock'}`} />
                {user.isAuthenticated ? 'Authenticated' : 'Pending'}
            </span>
        </div>
    );
};

function EcxOffcanvas({ show, handleClose }) {
    const userInfo = useEyeconxStore((state) => state.userInfo);
    const setUserInfo = useEyeconxStore((state) => state.setUserInfo);
    const user = useEyeconxStore((state) => state.user);

    const navigate = useNavigate();
    const initials = getInitials(userInfo);
    const roleLabel = formatRoleLabel(userInfo?.role || user?.role);
    const isEcp = (userInfo?.role || user?.role) === 'ECP';

    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [newInvoiceCount, setNewInvoiceCount] = useState(0);

    const refreshUserInfo = useCallback(() => {
        if (!user?.clinicId || !user?.ID) {
            return Promise.resolve();
        }

        setIsRefreshing(true);

        return clinicService
            .getUserInfo(user.clinicId, user.ID)
            .then((res) => setUserInfo(res.data))
            .catch(() => {})
            .finally(() => setIsRefreshing(false));
    }, [user?.clinicId, user?.ID, setUserInfo]);

    useEffect(() => {
        if (userInfo === null) {
            refreshUserInfo();
        }
    }, [userInfo, refreshUserInfo]);

    useEffect(() => {
        if (show) {
            refreshUserInfo();
        }
    }, [show, refreshUserInfo]);

    useEffect(() => {
        if (!show || !user?.clinicId) {
            return undefined;
        }

        let cancelled = false;

        clinicService
            .getDocuments(user.clinicId, 0, 100, INVOICE_QUERY)
            .then((response) => {
                if (!cancelled) {
                    setNewInvoiceCount(countNewInvoices(response.data?.content, user.clinicId));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setNewInvoiceCount(0);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [show, user?.clinicId]);

    useEffect(() => {
        if (!show) {
            return undefined;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                if (showProfileModal) {
                    setShowProfileModal(false);
                    return;
                }

                if (showReminderModal) {
                    setShowReminderModal(false);
                    return;
                }

                handleClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [show, handleClose, showProfileModal, showReminderModal]);

    const handleLogout = () => {
        handleClose();
        navigate('/logout');
    };

    const handleLinkClick = () => {
        handleClose();
    };

    const handleProfileSaved = (updatedUser) => {
        setUserInfo(updatedUser);
    };

    const reminderUser = userInfo?.id
        ? userInfo
        : user?.ID
            ? { id: user.ID, firstName: userInfo?.firstName, lastName: userInfo?.lastName }
            : null;

    return (
        <>
            {show && (
                <div
                    className="ecx-user-panel-backdrop"
                    onClick={handleClose}
                    aria-hidden="true"
                />
            )}

            <aside
                className={`offcanvas offcanvas-end ecx-user-panel ${show ? 'show' : ''}`}
                tabIndex="-1"
                style={{ visibility: show ? 'visible' : 'hidden' }}
                aria-hidden={!show}
                aria-label="Account panel"
            >
                <div className="ecx-user-panel__header">
                    <div className="ecx-user-panel__header-main">
                        <span className="ecx-nav-icon ecx-nav-icon--purple ecx-user-panel__header-icon" aria-hidden="true">
                            <i className="bi bi-person-circle" />
                        </span>
                        <div className="min-width-0">
                            <h5 className="ecx-user-panel__title mb-0">My Account</h5>
                            <p className="ecx-user-panel__subtitle mb-0">Profile and security settings</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn flex-shrink-0"
                        aria-label="Close account panel"
                        onClick={handleClose}
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                <div className="ecx-user-panel__body ecx-page-scroll">
                    <section className="ecx-user-panel__profile-card">
                        <span className="ecx-nav-icon ecx-nav-icon--primary ecx-user-panel__avatar" aria-hidden="true">
                            {initials || <i className="bi bi-person-fill" />}
                        </span>
                        <div className="ecx-user-panel__profile-content">
                            <h6 className="ecx-user-panel__name">
                                {userInfo?.firstName} {userInfo?.lastName}
                            </h6>
                            <div className="ecx-user-panel__profile-meta">
                                {roleLabel && (
                                    <span className="ecx-user-panel__role-chip">
                                        <span className="ecx-nav-icon ecx-nav-icon--indigo ecx-user-panel__role-chip-icon">
                                            <i className="bi bi-shield-check" />
                                        </span>
                                        {roleLabel}
                                    </span>
                                )}
                                {isRefreshing && (
                                    <span className="ecx-user-panel__refreshing">
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        Refreshing
                                    </span>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className="ecx-user-panel__status-card">
                        <h6 className="ecx-user-panel__section-title">Account Status</h6>
                        <UserStatusPills user={userInfo} />
                    </section>

                    <section className="ecx-user-panel__details">
                        <div className="ecx-user-panel__detail">
                            <span className="ecx-user-panel__detail-label">Email</span>
                            <span className="ecx-user-panel__detail-value" title={userInfo?.email}>
                                <span className="ecx-nav-icon ecx-nav-icon--cyan ecx-user-panel__detail-icon">
                                    <i className="bi bi-envelope" />
                                </span>
                                {userInfo?.email || '—'}
                            </span>
                        </div>
                    </section>

                    <section className="ecx-user-panel__section">
                        <h6 className="ecx-user-panel__section-title">Account Settings</h6>
                        <button
                            type="button"
                            className="ecx-user-panel__action ecx-user-panel__action--primary ecx-page-list-item w-100 border-0 text-start"
                            onClick={() => setShowProfileModal(true)}
                        >
                            <PanelActionIcon icon="bi-person-lines-fill" themeKey="primary" />
                            <span className="ecx-user-panel__action-copy">
                                <strong>Edit Profile</strong>
                                <small>Update your name and email</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </button>
                        <Link
                            to="/invoices"
                            className="ecx-user-panel__action ecx-user-panel__action--pink ecx-page-list-item"
                            onClick={handleLinkClick}
                        >
                            <PanelActionIcon icon="bi-receipt" themeKey="pink" />
                            <span className="ecx-user-panel__action-copy">
                                <strong>
                                    Invoices
                                    {newInvoiceCount > 0 && (
                                        <span className="ecx-user-panel__invoice-badge" aria-label={`${newInvoiceCount} new invoices`}>
                                            {newInvoiceCount}
                                        </span>
                                    )}
                                </strong>
                                <small>View and download clinic invoices</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </Link>
                        <Link
                            to="/pms-update-history"
                            className="ecx-user-panel__action ecx-user-panel__action--cyan ecx-page-list-item"
                            onClick={handleLinkClick}
                        >
                            <PanelActionIcon icon="bi-arrow-repeat" themeKey="cyan" />
                            <span className="ecx-user-panel__action-copy">
                                <strong>PMS Update History</strong>
                                <small>Review PMS sync logs and status</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </Link>
                        {isEcp && (
                            <button
                                type="button"
                                className="ecx-user-panel__action ecx-user-panel__action--orange ecx-page-list-item w-100 border-0 text-start"
                                onClick={() => setShowReminderModal(true)}
                            >
                                <PanelActionIcon icon="bi-bell" themeKey="orange" />
                                <span className="ecx-user-panel__action-copy">
                                    <strong>My Reminder Settings</strong>
                                    <small>Personal reminder preferences</small>
                                </span>
                                <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                            </button>
                        )}
                        <Link
                            to="/update-password"
                            className="ecx-user-panel__action ecx-user-panel__action--indigo ecx-page-list-item"
                            onClick={handleLinkClick}
                        >
                            <PanelActionIcon icon="bi-shield-lock" themeKey="indigo" />
                            <span className="ecx-user-panel__action-copy">
                                <strong>Password Settings</strong>
                                <small>Update your login password</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </Link>
                    </section>

                    <section className="ecx-user-panel__section">
                        <h6 className="ecx-user-panel__section-title">Support</h6>
                        <Link
                            to="/whats-new"
                            className="ecx-user-panel__action ecx-user-panel__action--info ecx-page-list-item"
                            onClick={handleLinkClick}
                        >
                            <PanelActionIcon icon="bi-stars" themeKey="info" />
                            <span className="ecx-user-panel__action-copy">
                                <strong>What&apos;s New</strong>
                                <small>See the latest features</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </Link>
                        <Link
                            to="/help"
                            className="ecx-user-panel__action ecx-user-panel__action--info ecx-page-list-item"
                            onClick={handleLinkClick}
                        >
                            <PanelActionIcon icon="bi-question-circle" themeKey="info" />
                            <span className="ecx-user-panel__action-copy">
                                <strong>Help &amp; Support</strong>
                                <small>Get help with EyeconX</small>
                            </span>
                            <i className="bi bi-chevron-right ecx-user-panel__action-chevron" />
                        </Link>
                    </section>
                </div>

                <div className="ecx-user-panel__footer">
                    <button
                        type="button"
                        className="btn btn-outline-danger ecx-logout-btn w-100 py-2 d-flex align-items-center justify-content-center"
                        onClick={handleLogout}
                    >
                        <span>Logout</span>
                        <i className="bi bi-box-arrow-right ms-2" />
                    </button>
                </div>
            </aside>

            <EcxUserProfileModal
                show={showProfileModal}
                userInfo={userInfo}
                clinicId={user?.clinicId}
                userId={user?.ID}
                onClose={() => setShowProfileModal(false)}
                onSaved={handleProfileSaved}
            />

            {showReminderModal && (
                <UserReminder
                    show={showReminderModal}
                    user={reminderUser}
                    onClose={() => setShowReminderModal(false)}
                />
            )}
        </>
    );
}

export default EcxOffcanvas;
